import {
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import CancelSessionIcon from '../../../assets/SessionIcon/cancel-session-icon.svg';
import ReportIcon from '../../../assets/SessionIcon/report-issue-icon.svg';
import './GroupSessionDetails.css';
import OneOnOneSessionIcon from '../../../assets/Dashboard/oneoneonesession.svg';
import GroupSessionIcon from '../../../assets/Dashboard/group.svg';
import PrimeSessionIcon from '../../../assets/Dashboard/prime.svg';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import dayjs from 'dayjs';
import { useTranslation } from '../../../i18n/LanguageContext';
import CancelSessionModal from '../CancelSessionModal/CancelSessionModal';
import ReportModal from '../ReportModal/ReportModal';
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';
import VideocamIcon from '@mui/icons-material/Videocam';
import StarIcon from '@mui/icons-material/Star';
// import ReportIcon from '@mui/icons-material/Report';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import ButtonMui from '@mui/material/Button';
import { axiosInstance } from '../../../utils/tokenUtils';
import { toast } from 'react-hot-toast';

const SessionTypeColor = {
  one_on_one: {
    color: '#6B60E7',
    bgcolor: '#F0EFFF',
    value: 'One-On-One Session',
    icon: OneOnOneSessionIcon,
  },
  group: {
    color: '#7BCF8C',
    bgcolor: '#F2FFF5',
    value: 'Group Session',
    icon: GroupSessionIcon,
  },
  prime: {
    color: '#E0AD14',
    bgcolor: '#FFFBF0',
    value: 'Prime Session',
    icon: PrimeSessionIcon,
  },
};

const LevelToTeachColor = {
  elementary: {
    color: '#14B334',
    bgcolor: '#F2FFF5',
    value: 'Elementary Level',
  },
  university: {
    color: '#14B334',
    bgcolor: '#F2FFF5',
    value: 'University',
  },
  high_school: { color: '#E0AD14', bgcolor: '#FFFBF0', value: 'High School' },
  middle_school: {
    color: '#6B60E7',
    bgcolor: '#F0EFFF',
    value: 'Middle School',
  },
  professional: {
    color: '#0A5EF3',
    bgcolor: '#E8F0FF',
    value: 'Professional',
  },
};

const GroupSessionDetails = ({ singleSessionDetails }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const navigate = useNavigate();
  const { translate } = useTranslation();
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [openReportModal, setOpenReportModal] = useState(false);
  const [alreadyDisputedOpen, setAlreadyDisputedOpen] = useState(false);

  const handleMenuClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const checkDisputeThenOpen = async () => {
    try {
      const res = await axiosInstance.get(`/teachers/sessions/dispute/${singleSessionDetails?.id}`);
      if (res?.data && !res.data?.detail) {
        setAlreadyDisputedOpen(true);
        return;
      }
      if (res?.data?.detail === 'Dispute not found for this session') {
        setOpenReportModal(true);
        return;
      }
      setAlreadyDisputedOpen(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === 'Dispute not found for this session' || err?.response?.status === 404) {
        setOpenReportModal(true);
      } else {
        setAlreadyDisputedOpen(true);
      }
    }
  };

  // Convert a UTC timestamp string to local Dayjs
  const toLocalFromUTC = utcString => {
    const utc = dayjs(utcString);
    return dayjs(new Date(utc.valueOf() - utc.toDate().getTimezoneOffset() * 60000));
  };

  const isWithinJoinWindow = () => {
    if (!singleSessionDetails?.start_time) return false;
    const startTimeLocal = toLocalFromUTC(singleSessionDetails.start_time);
    const now = dayjs();
    const sessionLength = Number(singleSessionDetails.length_in_minutes || 0);
    return (
      startTimeLocal.diff(now, 'minute') <= 20 &&
      startTimeLocal.diff(now, 'minute') >= -sessionLength
    );
  };

  const handleAction = action => {
    if (action === 'reschedule') {
      // not available for group on mobile; keep hidden
    } else if (action === 'cancel') {
      setOpenCancelModal(true);
    } else if (action === 'report') {
      checkDisputeThenOpen();
    }
    handleMenuClose();
  };

  const formatTimeInterval = (startTime, lengthInMinutes) => {
    if (!startTime || !lengthInMinutes) return '---';

    // Convert UTC start time to local
    const startTimeLocal = toLocalFromUTC(startTime);

    // Calculate end time based on start time and session length
    const endTimeLocal = dayjs(startTimeLocal).add(lengthInMinutes, 'minutes');

    // Format start and end times
    const startFormat = startTimeLocal.format('h:mm a');
    const endFormat = endTimeLocal.format('h:mm a');

    // Return formatted time range
    return `${startFormat} - ${endFormat}`;
  };

  const handleJoinSession = () => {
    const link = singleSessionDetails?.meeting_link;
    if (!link) {
      toast.error('Please check back at the session time');
      return;
    }
    if (!isWithinJoinWindow()) {
      toast.info('Please join at the scheduled session time');
    }
    window.open(link, '_blank');
  };

  const renderLearnersOverview = () => {
    if (!singleSessionDetails?.students || singleSessionDetails.students.length === 0) {
      return <Typography>No students in this session</Typography>;
    }

    return (
      <Box className="session-detail-section">
        <Typography variant="h6" className="section-title">
          Learners Overview
        </Typography>
        <Box className="detail-grid">
          <Box className="detail-item">
            <Typography className="detail-label">Total Learners</Typography>
            <Typography className="detail-value">
              {singleSessionDetails?.students?.length || 0}
            </Typography>
          </Box>
          <Box className="detail-item">
            <Typography className="detail-label">School Levels</Typography>
            <Typography className="detail-value">
              {(() => {
                const levels = Array.from(
                  new Set(
                    singleSessionDetails?.students
                      ?.map(student => student?.school_level)
                      .filter(level => level)
                  )
                )
                  .map(level => LevelToTeachColor[level]?.value || level)
                  .join(', ');
                return levels || 'School Level Not Available';
              })()}
            </Typography>
          </Box>
          <Box className="detail-item">
            <Typography className="detail-label">Countries</Typography>
            <Typography className="detail-value">
              {Array.from(
                new Set(singleSessionDetails?.students?.map(student => student?.user?.country))
              )
                .filter(Boolean)
                .join(', ') || '---'}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderSessionDetails = () => {
    return (
      <Box className="session-detail-section">
        <Typography variant="h6" className="section-title">
          Session Detail
        </Typography>
        <Box className="detail-grid">
          <Box className="detail-item">
            <Typography className="detail-label">Subject</Typography>
            <Typography className="detail-value">
              {singleSessionDetails?.subject || '---'}
            </Typography>
          </Box>
          <Box className="detail-item">
            <Typography className="detail-label">Date</Typography>
            <Typography className="detail-value">
              {singleSessionDetails?.start_time
                ? (() => {
                    // Convert UTC to local time
                    const startTimeUTC = dayjs(singleSessionDetails.start_time);
                    const startTimeLocal = dayjs(
                      new Date(
                        startTimeUTC.valueOf() - startTimeUTC.toDate().getTimezoneOffset() * 60000
                      )
                    );
                    return startTimeLocal.format('ddd D MMM, YYYY');
                  })()
                : '---'}
            </Typography>
          </Box>
          <Box className="detail-item">
            <Typography className="detail-label">Timing</Typography>
            <Typography className="detail-value">
              {singleSessionDetails?.start_time
                ? formatTimeInterval(
                    singleSessionDetails?.start_time,
                    singleSessionDetails?.length_in_minutes
                  )
                : '---'}
            </Typography>
          </Box>
          <Box className="detail-item">
            <Typography className="detail-label">Session Type</Typography>
            <Typography
              sx={{
                color: `${SessionTypeColor[singleSessionDetails?.session_type]?.color} !important`,
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              {singleSessionDetails?.session_type
                ? SessionTypeColor[singleSessionDetails?.session_type]?.value
                : '---'}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderCancellationDetails = () => {
    if (singleSessionDetails?.status !== 'cancelled') return null;

    return (
      <>
        <Box className="session-detail-section">
          <Typography variant="h6" className="section-title">
            Cancellation Details
          </Typography>
          <Box className="detail-grid">
            <Box className="detail-item">
              <Typography className="detail-label">Cancelled by</Typography>
              <Typography className="cancelled-detail-value">
                {singleSessionDetails?.cancelled_by === 'teacher'
                  ? 'Instructor'
                  : singleSessionDetails?.cancelled_by === 'student'
                    ? 'Learner'
                    : '---'}
              </Typography>
            </Box>
            <Box className="detail-item">
              <Typography className="detail-label">Cancelled on</Typography>
              <Typography className="cancelled-detail-value">
                {singleSessionDetails?.updated_at
                  ? (() => {
                      // Convert UTC to local time
                      const updatedTimeUTC = dayjs(singleSessionDetails.updated_at);
                      const updatedTimeLocal = dayjs(
                        new Date(
                          updatedTimeUTC.valueOf() -
                            updatedTimeUTC.toDate().getTimezoneOffset() * 60000
                        )
                      );
                      return updatedTimeLocal.format('ddd D MMM, YYYY');
                    })()
                  : '---'}
              </Typography>
            </Box>
            <Box className="detail-item">
              <Typography className="detail-label">Cancelled Time</Typography>
              <Typography className="cancelled-detail-value" style={{ color: '#E14949' }}>
                {singleSessionDetails?.updated_at
                  ? (() => {
                      // Convert UTC to local time
                      const updatedTimeUTC = dayjs(singleSessionDetails.updated_at);
                      const updatedTimeLocal = dayjs(
                        new Date(
                          updatedTimeUTC.valueOf() -
                            updatedTimeUTC.toDate().getTimezoneOffset() * 60000
                        )
                      );
                      return updatedTimeLocal.format('h:mm A');
                    })()
                  : '---'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box className="session-detail-section">
          <Typography variant="h6" className="section-title">
            Cancellation Reason
          </Typography>
          <Box className="detail-grid">
            <Box className="detail-item" style={{ width: '100%' }}>
              <Typography className="detail-value">
                {singleSessionDetails?.cancellation_reason || 'No reason provided'}
              </Typography>
            </Box>
          </Box>
        </Box>

        {singleSessionDetails.penalty_koins > 0 && (
          <Box className="session-detail-section">
            <Typography variant="h6" className="section-title">
              Koins Information
            </Typography>
            <Box className="detail-grid">
              <Box className="detail-item" style={{ width: '100%' }}>
                <Typography className="koins-text">
                  Koins - <span style={{ fontWeight: 'bold' }}>Deducted</span>
                </Typography>
                <Typography className="koins-detail">
                  Since the cancellation was done within{' '}
                  <span style={{ fontWeight: 'bold' }}>8 hours</span>,
                  <span style={{ fontWeight: 'bold' }}>
                    {' '}
                    {singleSessionDetails.penalty_koins || 0} koins
                  </span>{' '}
                  has been deducted from your wallet as
                  <span style={{ color: '#E14949', fontWeight: 'bold' }}> penalties</span>.
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </>
    );
  };

  const renderStudentCards = () => {
    if (!singleSessionDetails?.students || singleSessionDetails.students.length === 0) {
      return <Typography>No students in this session</Typography>;
    }

    return (
      <Box className="session-detail-section">
        <Typography variant="h6" className="section-title">
          Learner Profile
        </Typography>

        {singleSessionDetails.students.map(student => (
          <Box className="student-card" key={student?.id}>
            <Box className="student-header">
              <Box className="student-info">
                <Avatar src={student?.user?.profile_image} sx={{ width: '45px', height: '45px' }} />
                <Box className="student-main-info">
                  <Box className="student-name-container">
                    <Typography className="student-name">{student?.user?.name || '---'}</Typography>
                    {singleSessionDetails?.session_type && (
                      <>
                        <Divider orientation="vertical" className="divider" />
                        <Box
                          className="session-type-badge"
                          style={{
                            color: SessionTypeColor[singleSessionDetails?.session_type]?.color,
                          }}
                        >
                          <img
                            src={SessionTypeColor[singleSessionDetails?.session_type]?.icon}
                            alt={SessionTypeColor[singleSessionDetails?.session_type]?.value}
                          />
                          <Typography
                            style={{
                              color: SessionTypeColor[singleSessionDetails?.session_type]?.color,
                            }}
                          >
                            {SessionTypeColor[singleSessionDetails?.session_type]?.value}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Box>
                  <Box
                    className="school-level-badge"
                    style={{
                      backgroundColor: student?.school_level
                        ? LevelToTeachColor[student?.school_level]?.bgcolor
                        : '#F5F6F7',
                      color: student?.school_level
                        ? LevelToTeachColor[student?.school_level]?.color
                        : '#949DA6',
                      fontStyle: student?.school_level ? 'normal' : 'italic',
                    }}
                  >
                    <Typography style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                      {student?.school_level
                        ? LevelToTeachColor[student?.school_level]?.value ||
                          student?.school_level
                            .replace(/_/g, ' ')
                            .replace(/\b\w/g, l => l.toUpperCase())
                        : 'School Level Not Available'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box className="student-detail-section">
              <Box className="student-details-grid">
                <Box className="student-detail-item">
                  <Typography className="detail-label">Name</Typography>
                  <Typography className="detail-value">{student?.user?.name || '---'}</Typography>
                </Box>
                <Box className="student-detail-item">
                  <Typography className="detail-label">Country</Typography>
                  <Typography className="detail-value">
                    {student?.user?.country || '---'}
                  </Typography>
                </Box>
                <Box className="student-detail-item">
                  <Typography className="detail-label">School Name</Typography>
                  <Typography className="detail-value">{student?.school_name || '---'}</Typography>
                </Box>
                <Box className="student-detail-item">
                  <Typography className="detail-label">School Level</Typography>
                  <Typography
                    className="detail-value"
                    sx={{
                      color: student?.school_level
                        ? LevelToTeachColor[student?.school_level]?.color
                        : '#949DA6',
                      fontStyle: student?.school_level ? 'normal' : 'italic',
                    }}
                  >
                    {student?.school_level
                      ? LevelToTeachColor[student?.school_level]?.value ||
                        student?.school_level
                          .replace(/_/g, ' ')
                          .replace(/\b\w/g, l => l.toUpperCase())
                      : '---'}
                  </Typography>
                </Box>
                <Box className="student-detail-item">
                  <Typography className="detail-label">Gender</Typography>
                  <Box className="flex items-center gap-1">
                    {student?.user?.gender === 'female' ? (
                      <>
                        <FemaleIcon sx={{ color: '#E83E8C', fontSize: 20 }} />
                        <Typography className="detail-value" sx={{ color: '#E83E8C' }}>
                          Female
                        </Typography>
                      </>
                    ) : student?.user?.gender === 'male' ? (
                      <>
                        <MaleIcon sx={{ color: '#007BFF', fontSize: 20 }} />
                        <Typography className="detail-value" sx={{ color: '#007BFF' }}>
                          Male
                        </Typography>
                      </>
                    ) : (
                      <Typography className="detail-value">---</Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  // Only show reviews for completed one-on-one on mobile; keep group hidden
  const renderReviews = () => null;

  const renderCompletedGroupSession = () => {
    if (singleSessionDetails?.status !== 'completed') return null;

    return (
      <>
        <Box className="session-detail-section">
          <Typography variant="h6" className="section-title">
            Session Detail
          </Typography>
          <Box className="detail-grid">
            <Box className="detail-item">
              <Typography className="detail-label">Subject</Typography>
              <Typography className="detail-value">
                {singleSessionDetails?.subject || '---'}
              </Typography>
            </Box>
            <Box className="detail-item">
              <Typography className="detail-label">Date</Typography>
              <Typography className="detail-value">
                {singleSessionDetails?.start_time
                  ? (() => {
                      // Convert UTC to local time
                      const startTimeUTC = dayjs(singleSessionDetails.start_time);
                      const startTimeLocal = dayjs(
                        new Date(
                          startTimeUTC.valueOf() - startTimeUTC.toDate().getTimezoneOffset() * 60000
                        )
                      );
                      return startTimeLocal.format('ddd D MMM, YYYY');
                    })()
                  : '---'}
              </Typography>
            </Box>
            <Box className="detail-item">
              <Typography className="detail-label">Timing</Typography>
              <Typography className="detail-value">
                {singleSessionDetails?.start_time
                  ? formatTimeInterval(
                      singleSessionDetails?.start_time,
                      singleSessionDetails?.length_in_minutes
                    )
                  : '---'}
              </Typography>
            </Box>
            <Box className="detail-item">
              <Typography className="detail-label">Session Type</Typography>
              <Typography
                className="detail-value"
                sx={{
                  color: `${SessionTypeColor[singleSessionDetails?.session_type]?.color} !important`,
                }}
              >
                {singleSessionDetails?.session_type
                  ? SessionTypeColor[singleSessionDetails?.session_type]?.value
                  : '---'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Typography className="learner-section-title">Learner Profile</Typography>

        {singleSessionDetails.students && singleSessionDetails.students.length > 0 ? (
          singleSessionDetails.students.map((student, index) => (
            <Box key={student.id || index} className="student-card">
              <Box className="student-header">
                <Box className="student-info">
                  <Avatar
                    src={student?.user?.profile_image}
                    alt={student?.user?.name || 'Student'}
                    sx={{ width: '45px', height: '45px' }}
                  />
                  <Box className="student-main-info">
                    <Box className="student-name-container">
                      <Typography className="student-name">
                        {student?.user?.name || '---'}
                      </Typography>
                      <Box
                        className="session-type-badge"
                        style={{
                          color: SessionTypeColor[singleSessionDetails?.session_type]?.color,
                        }}
                      >
                        <img
                          src={SessionTypeColor[singleSessionDetails?.session_type]?.icon}
                          alt={SessionTypeColor[singleSessionDetails?.session_type]?.value}
                        />
                        <Typography
                          className="session-type-text"
                          style={{
                            color: SessionTypeColor[singleSessionDetails?.session_type]?.color,
                          }}
                        >
                          {SessionTypeColor[singleSessionDetails?.session_type]?.value}
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      className="school-level-badge"
                      style={{
                        backgroundColor: student?.school_level
                          ? LevelToTeachColor[student?.school_level]?.bgcolor
                          : '#F5F6F7',
                        color: student?.school_level
                          ? LevelToTeachColor[student?.school_level]?.color
                          : '#949DA6',
                        fontStyle: student?.school_level ? 'normal' : 'italic',
                      }}
                    >
                      <Typography style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                        {student?.school_level
                          ? LevelToTeachColor[student?.school_level]?.value ||
                            student?.school_level
                              .replace(/_/g, ' ')
                              .replace(/\b\w/g, l => l.toUpperCase())
                          : 'School Level Not Available'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box className="student-detail-section">
                <Box className="student-details-grid">
                  <Box className="student-detail-item">
                    <Typography className="detail-label">Name</Typography>
                    <Typography className="detail-value">{student?.user?.name || '---'}</Typography>
                  </Box>
                  <Box className="student-detail-item">
                    <Typography className="detail-label">Country</Typography>
                    <Typography className="detail-value">
                      {student?.user?.country || '---'}
                    </Typography>
                  </Box>
                  <Box className="student-detail-item">
                    <Typography className="detail-label">School Name</Typography>
                    <Typography className="detail-value">
                      {student?.school_name || '---'}
                    </Typography>
                  </Box>
                  <Box className="student-detail-item">
                    <Typography className="detail-label">School Level</Typography>
                    <Typography className="detail-value">
                      {student?.school_level
                        ? LevelToTeachColor[student?.school_level]?.value ||
                          student?.school_level
                            .replace(/_/g, ' ')
                            .replace(/\b\w/g, l => l.toUpperCase())
                        : '---'}
                    </Typography>
                  </Box>
                  <Box className="student-detail-item">
                    <Typography className="detail-label">Gender</Typography>
                    <Box className="gender-display">
                      {student?.user?.gender === 'female' ? (
                        <>
                          <FemaleIcon style={{ color: '#E83E8C', fontSize: 20 }} />
                          <Typography className="detail-value" style={{ color: '#E83E8C' }}>
                            Female
                          </Typography>
                        </>
                      ) : student?.user?.gender === 'male' ? (
                        <>
                          <MaleIcon style={{ color: '#007BFF', fontSize: 20 }} />
                          <Typography className="detail-value" style={{ color: '#007BFF' }}>
                            Male
                          </Typography>
                        </>
                      ) : (
                        <Typography className="detail-value">---</Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          ))
        ) : (
          <Typography className="no-students-text">No students in this session</Typography>
        )}
      </>
    );
  };

  return (
    <>
      <Box className="session-details-container">
        <Box className="session-header">
          <Box className="breadcrumb">
            <Typography
              className="breadcrumb-text"
              onClick={() => navigate('/instructor/sessions')}
            >
              {translate('sessionDetails', 'sessionText')} {'>'}
              {'>'}{' '}
              {singleSessionDetails?.status === 'upcoming'
                ? `${translate('sessionDetails', 'upcomingText')}`
                : singleSessionDetails?.status === 'completed'
                  ? `${translate('sessionDetails', 'completedText')}`
                  : `${translate('sessionDetails', 'cancelledText')}`}{' '}
              {'>'}
              {'>'} Learners Profile {'>'}
              {'>'}
            </Typography>
            <Typography className="details-text">
              {translate('sessionDetails', 'detailsText')}
            </Typography>
          </Box>

          <Box className="header-actions">
            {singleSessionDetails?.status !== 'cancelled' && (
              <IconButton onClick={e => handleMenuClick(e)} className="more-options-button">
                {anchorEl ? (
                  <MoreHorizOutlinedIcon sx={{ color: '#0A5EF3' }} />
                ) : (
                  <MoreHorizOutlinedIcon />
                )}
              </IconButton>
            )}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                sx: { width: '200px' },
              }}
            >
              {/* For group sessions: only Cancel when upcoming or rescheduled; no reschedule option */}
              {singleSessionDetails?.session_type === 'group' &&
                (singleSessionDetails?.status === 'upcoming' ||
                  singleSessionDetails?.status === 'rescheduled' ||
                  singleSessionDetails?.status === 'resheduled') && (
                  <MenuItem onClick={() => handleAction('cancel')}>
                    <Box className="flex gap-2 items-center">
                      <img src={CancelSessionIcon} alt="Cancel" style={{ width: 24, height: 24 }} />
                      <div
                        style={{
                          color: 'var(--Acc5, #E14949)',
                          fontFamily: 'Poppins',
                          fontSize: '14px',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          lineHeight: '20px',
                        }}
                      >
                        {translate('sessionDetails', 'cancelSessionText')}
                      </div>
                    </Box>
                  </MenuItem>
                )}

              {/* Show Report for completed sessions */}
              {singleSessionDetails?.status === 'completed' && (
                <MenuItem onClick={() => handleAction('report')}>
                  <Box className="flex gap-2 items-center">
                    <img src={ReportIcon} alt="Report" />
                    <div>{translate('sessionDetails', 'reportIssuesText')}</div>
                  </Box>
                </MenuItem>
              )}
            </Menu>
          </Box>
        </Box>

        {/* Render different content based on session status */}
        {singleSessionDetails?.status === 'completed' ? (
          <>
            {renderCompletedGroupSession()}
            {renderReviews()}
          </>
        ) : (
          <>
            {renderSessionDetails()}
            {renderCancellationDetails()}
            {renderLearnersOverview()}
            {renderStudentCards()}
          </>
        )}

        {singleSessionDetails?.status !== 'cancelled' &&
          singleSessionDetails?.status !== 'completed' && (
            <Box className="join-button-container">
              <Button
                variant="contained"
                color="primary"
                startIcon={<VideocamIcon />}
                onClick={handleJoinSession}
                className="join-zoom-button"
              >
                Start Session
              </Button>
            </Box>
          )}
      </Box>

      <CancelSessionModal
        open={openCancelModal}
        onClose={() => setOpenCancelModal(false)}
        sessionId={singleSessionDetails?.id}
        sessionType="group"
      />
      <ReportModal
        open={openReportModal}
        onClose={() => setOpenReportModal(false)}
        sessionDetails={singleSessionDetails}
        onSuccess={() => {
          console.log('Report submitted successfully');
        }}
      />

      <Dialog open={alreadyDisputedOpen} onClose={() => setAlreadyDisputedOpen(false)}>
        <DialogTitle>You have already raised the dispute</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            A dispute has already been created for this session.
          </Typography>
        </DialogContent>
        <DialogActions>
          <ButtonMui onClick={() => setAlreadyDisputedOpen(false)}>OK</ButtonMui>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GroupSessionDetails;
